import { Minus, Plus } from "lucide-react";
import type { PointerEvent as ReactPointerEvent } from "react";
import { useEffect, useRef, useState } from "react";
import { oklchToCss, oklchToHex, type OklchColor } from "@/document/model/color";
import { tryParseColorString } from "@/document/model/colorString";
import {
  colorAtGradientPosition,
  createGradientStop,
  normalizeGradientPaint,
  sampleGradientPaint,
  type GradientPaint,
  type GradientColorSpace,
} from "@/document/model/styles";
import { Metric } from "./Metric";
import { PanelSelect, PanelSelectItem } from "./PanelSelect";
import { queueSelectInputText } from "./inputFocus";

export function GradientEditor({
  paint,
  selectedStopId,
  onSelectedStopChange,
  onStopColorClick,
  onChange,
}: {
  paint: GradientPaint;
  selectedStopId?: string;
  onSelectedStopChange: (stopId: string) => void;
  onStopColorClick: (stopId: string) => void;
  onChange: (paint: GradientPaint) => void;
}) {
  const normalized = normalizeGradientPaint(paint);
  const activeStopId = selectedStopId && normalized.stops.some(stop => stop.id === selectedStopId)
    ? selectedStopId
    : normalized.stops[0]?.id;
  const rampRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (activeStopId && activeStopId !== selectedStopId) {
      onSelectedStopChange(activeStopId);
    }
  }, [activeStopId, onSelectedStopChange, selectedStopId]);

  return (
    <div
      className="grid gap-3"
      onKeyDown={event => {
        if (event.key !== "Delete" && event.key !== "Backspace") {
          return;
        }

        const target = event.target as HTMLElement | null;
        if (target && isTextEditingElement(target)) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        deleteStop(activeStopId);
      }}
    >
      <div className={`grid gap-2 ${normalized.type === "linearGradient" ? "grid-cols-[1fr_72px]" : "grid-cols-1"}`}>
        <PanelSelect
          value={normalized.type}
          onValueChange={value => commit(convertGradientType(normalized, value === "radialGradient" ? "radialGradient" : "linearGradient"))}
        >
          <PanelSelectItem value="linearGradient">Linear</PanelSelectItem>
          <PanelSelectItem value="radialGradient">Radial</PanelSelectItem>
        </PanelSelect>
        {normalized.type === "linearGradient" ? (
          <Metric
            label="Deg"
            value={normalized.angle}
            disabled={false}
            compact
            onCommit={angle => commit({ ...normalized, angle: normalizeAngle(angle), start: undefined, end: undefined })}
          />
        ) : null}
      </div>

      <div className="grid grid-cols-[1fr_96px] gap-2">
        <div
          ref={rampRef}
          className="relative h-12 rounded-md border border-white/10 bg-black"
          style={{ background: gradientStopStripCss(normalized) }}
          onPointerDown={event => {
            if (event.target !== rampRef.current) {
              return;
            }

            const position = pointerPosition(event);
            const stop = createGradientStop(position, colorAtGradientPosition(normalized, position));
            commit({ ...normalized, stops: [...normalized.stops, stop] });
            onSelectedStopChange(stop.id);
          }}
        >
          {normalized.stops.map(stop => (
            <button
              key={stop.id}
              type="button"
              aria-label="Gradient stop"
              className={`absolute top-1/2 size-4 -translate-x-1/2 -translate-y-1/2 rounded-[4px] border shadow-sm ${stop.id === activeStopId ? "border-sky-300 ring-2 ring-sky-500/80" : "border-white/50"}`}
              style={{ left: `${stop.position * 100}%`, background: oklchToCss(stop.color) }}
              onPointerDown={event => {
                event.stopPropagation();
                onSelectedStopChange(stop.id);
                beginDrag(stop.id, event);
              }}
            />
          ))}
        </div>

        <PanelSelect
          value={normalized.colorSpace}
          onValueChange={value => commit({ ...normalized, colorSpace: value as GradientColorSpace })}
          triggerClassName="h-12 uppercase"
        >
          <PanelSelectItem value="rgb">RGB</PanelSelectItem>
          <PanelSelectItem value="oklch">OKLCH</PanelSelectItem>
        </PanelSelect>
      </div>

      <div className="grid gap-1">
        <div className="flex items-center justify-between text-[11px] uppercase text-slate-500">
          <span>Stops</span>
          <button
            type="button"
            className="rounded p-1 text-slate-300 hover:bg-white/10"
            onClick={() => {
              const stop = createGradientStop(0.5, colorAtGradientPosition(normalized, 0.5));
              commit({ ...normalized, stops: [...normalized.stops, stop] });
              onSelectedStopChange(stop.id);
            }}
          >
            <Plus className="size-3.5" />
          </button>
        </div>

        {normalized.stops.map(stop => (
          <div
            key={stop.id}
            className={`grid grid-cols-[48px_20px_1fr_52px_24px] items-center gap-1 rounded px-1.5 py-1 ${stop.id === activeStopId ? "bg-sky-500/25" : "bg-white/[0.04]"}`}
          >
            <input
              aria-label="Stop position"
              className="h-6 rounded bg-black/20 px-1 text-left font-mono text-[11px] text-slate-200 outline-none focus:bg-black/30"
              value={Math.round(stop.position * 100)}
              onFocus={event => {
                onSelectedStopChange(stop.id);
                queueSelectInputText(event.currentTarget);
              }}
              onChange={event => updateStop(stop.id, { position: percentageValue(event.target.value) })}
            />
            <button
              type="button"
              aria-label="Stop color"
              className="size-4 rounded-[3px] border border-white/20 shadow-sm"
              style={{ background: oklchToCss(stop.color) }}
              onClick={() => {
                onSelectedStopChange(stop.id);
                onStopColorClick(stop.id);
              }}
            />
            <StopColorInput
              color={stop.color}
              onFocus={() => {
                onSelectedStopChange(stop.id);
              }}
              onCommit={color => updateStop(stop.id, { color })}
            />
            <input
              aria-label="Stop opacity"
              className="h-6 rounded bg-black/20 px-1 text-left font-mono text-[11px] text-slate-200 outline-none focus:bg-black/30"
              value={Math.round(stop.color.alpha * 100)}
              onFocus={event => {
                onSelectedStopChange(stop.id);
                queueSelectInputText(event.currentTarget);
              }}
              onChange={event => updateStop(stop.id, { color: colorWithOpacity(stop.color, percentageValue(event.target.value)) })}
            />
            <button
              type="button"
              aria-label="Remove stop"
              disabled={normalized.stops.length <= 2}
              className="rounded p-1 text-slate-400 hover:bg-white/10 disabled:opacity-30"
              onClick={() => deleteStop(stop.id)}
            >
              <Minus className="size-3.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  function beginDrag(stopId: string, event: ReactPointerEvent): void {
    rampRef.current?.setPointerCapture(event.pointerId);
    moveStop(stopId, event);
    const handlePointerMove = (moveEvent: PointerEvent) => moveStop(stopId, moveEvent);
    const handlePointerUp = () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp, { once: true });
  }

  function moveStop(stopId: string, event: PointerEvent | ReactPointerEvent): void {
    updateStop(stopId, { position: pointerPosition(event) });
  }

  function updateStop(stopId: string, patch: Partial<{ position: number; color: OklchColor }>): void {
    onSelectedStopChange(stopId);
    commit({
      ...normalized,
      stops: normalized.stops.map(stop => stop.id === stopId
        ? {
            ...stop,
            position: patch.position ?? stop.position,
            color: patch.color ?? stop.color,
          }
        : stop),
    });
  }

  function deleteStop(stopId?: string): void {
    if (!stopId || normalized.stops.length <= 2) {
      return;
    }

    const stopIndex = normalized.stops.findIndex(stop => stop.id === stopId);
    if (stopIndex < 0) {
      return;
    }

    const nextStops = normalized.stops.filter(stop => stop.id !== stopId);
    const nextSelectedStop = nextStops[Math.min(stopIndex, nextStops.length - 1)] ?? nextStops[0];
    commit({ ...normalized, stops: nextStops });
    onSelectedStopChange(nextSelectedStop?.id ?? "");
  }

  function commit(nextPaint: GradientPaint): void {
    onChange(normalizeGradientPaint(nextPaint));
  }

  function pointerPosition(event: PointerEvent | ReactPointerEvent): number {
    const rect = rampRef.current?.getBoundingClientRect();
    if (!rect || rect.width <= 0) {
      return 0;
    }

    return clamp01((event.clientX - rect.left) / rect.width);
  }
}

function StopColorInput({
  color,
  onFocus,
  onCommit,
}: {
  color: OklchColor;
  onFocus: () => void;
  onCommit: (color: OklchColor) => void;
}) {
  const committed = stopHexLabel(color);
  const [draft, setDraft] = useState(committed);
  const [editing, setEditing] = useState(false);
  const skipBlurCommitRef = useRef(false);

  useEffect(() => {
    if (!editing) {
      setDraft(committed);
    }
  }, [committed, editing]);

  function commit(value: string): void {
    const parsed = tryParseColorString(value);
    if (parsed) {
      const next = { ...parsed, alpha: color.alpha };
      onCommit(next);
      setDraft(stopHexLabel(next));
      return;
    }

    setDraft(committed);
  }

  return (
    <input
      aria-label="Stop color value"
      className="h-6 min-w-0 truncate rounded bg-black/20 px-2 text-left font-mono text-[11px] text-slate-200 outline-none hover:bg-black/30 focus:bg-black/30"
      value={draft}
      onFocus={event => {
        setEditing(true);
        onFocus();
        queueSelectInputText(event.currentTarget);
      }}
      onChange={event => setDraft(event.target.value)}
      onBlur={() => {
        setEditing(false);
        if (skipBlurCommitRef.current) {
          skipBlurCommitRef.current = false;
          return;
        }
        commit(draft);
      }}
      onKeyDown={event => {
        if (event.key === "Enter") {
          event.preventDefault();
          event.currentTarget.blur();
        }
        if (event.key === "Escape") {
          event.preventDefault();
          skipBlurCommitRef.current = true;
          setDraft(committed);
          setEditing(false);
          event.currentTarget.blur();
        }
      }}
    />
  );
}

export function gradientCss(paint: GradientPaint): string {
  const normalized = normalizeGradientPaint(paint);
  const stops = sampleGradientPaint(normalized, 24)
    .map(stop => `${oklchToCss(stop.color)} ${Math.round(stop.position * 100)}%`)
    .join(", ");
  return normalized.type === "radialGradient"
    ? `radial-gradient(circle, ${stops})`
    : `linear-gradient(${normalized.angle + 90}deg, ${stops})`;
}

function gradientStopStripCss(paint: GradientPaint): string {
  const normalized = normalizeGradientPaint(paint);
  const stops = sampleGradientPaint(normalized, 24)
    .map(stop => `${oklchToCss(stop.color)} ${Math.round(stop.position * 100)}%`)
    .join(", ");
  return `linear-gradient(to right, ${stops})`;
}

function convertGradientType(paint: GradientPaint, type: GradientPaint["type"]): GradientPaint {
  const normalized = normalizeGradientPaint(paint);
  if (normalized.type === type) {
    return normalized;
  }

  if (type === "radialGradient") {
    return {
      id: normalized.id,
      type,
      center: { x: 0.5, y: 0.5 },
      radius: 0.5,
      colorSpace: normalized.colorSpace,
      stops: normalized.stops,
    };
  }

  return {
    id: normalized.id,
    type,
    angle: 0,
    colorSpace: normalized.colorSpace,
    stops: normalized.stops,
  };
}

function percentageValue(value: string): number {
  return clamp01(Number.parseFloat(value) / 100);
}

function isTextEditingElement(element: HTMLElement): boolean {
  return element instanceof HTMLInputElement
    || element instanceof HTMLTextAreaElement
    || element.isContentEditable;
}

function stopHexLabel(color: OklchColor): string {
  return oklchToHex(color).replace("#", "").toUpperCase();
}

function colorWithOpacity(color: OklchColor, opacity: number): OklchColor {
  return {
    ...color,
    alpha: clamp01(opacity),
  };
}

function normalizeAngle(angle: number): number {
  return ((angle % 360) + 360) % 360;
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, Number.isFinite(value) ? value : 0));
}
